import {
  FileText,
  Keyboard,
  LogOut,
  Plus,
  Scale,
  Sparkles,
  UserPlus,
} from "lucide-react";
import type { ClientSummary, TaskSummary } from "@patent-studio/api-types";
import type { CommandGroup, CommandItem } from "@/components/ui/CommandPalette";
import {
  CHANGELOG_PATH,
  LEGAL_PATH,
  clientHubPath,
  initialsOf,
} from "../clients/paths";
import { draftOpeningPath } from "../stage-vocabulary";
import { resolveDraftTitle } from "./draft-title";

/** How many drafts and clients the palette opens on; the rest answer to a search. */
export const RECENT_SHOWN = 5;

/** What choosing an option does, for the palette to wire to its dialogs and the router. */
export interface PaletteActs {
  startDraftFor: (clientId: string) => void;
  chooseClientForDraft: () => void;
  addClient: () => void;
  addClientThenDraft: () => void;
  goTo: (path: string) => void;
  showShortcuts: () => void;
  signOut: () => void;
}

export interface PaletteData {
  /** The client whose page, or whose draft, is open. */
  here?: ClientSummary;
  clients: readonly ClientSummary[];
  /** The most recently updated first, as the server orders them. */
  drafts: readonly TaskSummary[];
}

function mostRecentFirst(clients: readonly ClientSummary[]): ClientSummary[] {
  return [...clients].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

function createCommands(
  here: ClientSummary | undefined,
  acts: PaletteActs,
): CommandItem[] {
  const newClient: CommandItem = {
    key: "new-client",
    label: "New client",
    icon: <UserPlus />,
    keywords: ["add", "create"],
    onSelect: acts.addClient,
  };
  if (here === undefined) {
    return [
      {
        key: "new-draft",
        label: "New draft…",
        hint: "Choose a client",
        icon: <Plus />,
        keywords: ["create", "start"],
        onSelect: acts.chooseClientForDraft,
      },
      newClient,
    ];
  }
  return [
    {
      key: "new-draft-here",
      label: `New draft for ${here.name}`,
      icon: <Plus />,
      keywords: ["create", "start"],
      onSelect: () => {
        acts.startDraftFor(here.client_id);
      },
    },
    {
      key: "new-draft",
      label: "New draft for another client…",
      icon: <Plus />,
      keywords: ["create", "start"],
      onSelect: acts.chooseClientForDraft,
    },
    newClient,
  ];
}

function draftCommands(
  drafts: readonly TaskSummary[],
  clients: readonly ClientSummary[],
  acts: PaletteActs,
): CommandItem[] {
  const nameOf = new Map(clients.map((c) => [c.client_id, c.name]));
  return drafts.map((draft, at) => {
    const client =
      draft.client_id === null ? undefined : nameOf.get(draft.client_id);
    return {
      key: `draft-${draft.task_id}`,
      label: resolveDraftTitle(draft),
      hint: draft.client_id === null ? "No client" : client,
      icon: <FileText />,
      keywords: client === undefined ? [] : [client],
      searchOnly: at >= RECENT_SHOWN,
      onSelect: () => {
        acts.goTo(draftOpeningPath(draft));
      },
    };
  });
}

function clientCommand(
  client: ClientSummary,
  onSelect: () => void,
): CommandItem {
  return {
    key: `client-${client.client_id}`,
    label: client.name,
    hint: client.industry,
    icon: initialsOf(client.name),
    onSelect,
  };
}

/** Everything the palette opens on: making something, then going to a draft or a client. */
export function mainCommands(
  { here, clients, drafts }: PaletteData,
  acts: PaletteActs,
): CommandGroup[] {
  return [
    { heading: "Create", items: createCommands(here, acts) },
    { heading: "Drafts", items: draftCommands(drafts, clients, acts) },
    {
      heading: "Clients",
      items: mostRecentFirst(clients).map((client, at) => ({
        ...clientCommand(client, () => {
          acts.goTo(clientHubPath(client.client_id));
        }),
        searchOnly: at >= RECENT_SHOWN,
      })),
    },
    { heading: "General", items: generalCommands(acts) },
  ];
}

/** What is there on every page, whatever is open. */
function generalCommands(acts: PaletteActs): CommandItem[] {
  return [
    {
      key: "legal",
      label: "Legal and policies",
      icon: <Scale />,
      keywords: ["terms", "privacy", "policy"],
      onSelect: () => {
        acts.goTo(LEGAL_PATH);
      },
    },
    {
      key: "whats-new",
      label: "What's new",
      icon: <Sparkles />,
      keywords: ["changelog", "change log", "release notes"],
      onSelect: () => {
        acts.goTo(CHANGELOG_PATH);
      },
    },
    {
      key: "shortcuts",
      label: "Keyboard shortcuts",
      icon: <Keyboard />,
      keywords: ["help", "keys"],
      onSelect: acts.showShortcuts,
    },
    {
      key: "sign-out",
      label: "Sign out",
      icon: <LogOut />,
      keywords: ["log out", "logout"],
      onSelect: acts.signOut,
    },
  ];
}

/** The second question a new draft asks: which client it is for, or a client that does not exist yet. */
export function clientStepCommands(
  clients: readonly ClientSummary[],
  acts: PaletteActs,
): CommandGroup[] {
  return [
    {
      heading: "Which client is this draft for?",
      items: mostRecentFirst(clients).map((client) =>
        clientCommand(client, () => {
          acts.startDraftFor(client.client_id);
        }),
      ),
    },
    {
      items: [
        {
          key: "add-client-then-draft",
          label: "Add a new client",
          icon: <UserPlus />,
          alwaysShown: true,
          onSelect: acts.addClientThenDraft,
        },
      ],
    },
  ];
}
